/* eslint-disable no-case-declarations */
import { documentToHtmlString } from '@contentful/rich-text-html-renderer'
import { BLOCKS, INLINES, MARKS } from '@contentful/rich-text-types'
import { $logger } from '@/boot/logger'
import { get, kebabCase } from 'lodash-es'

const richRenderOptions = {
  renderNode: {
    // Retain line breaks
    [BLOCKS.PARAGRAPH]: (node, next) => {
      const content = next(node.content)
      // Replace \n with <br/> but not inside <code>...</code> blocks
      const processed = content.replace(/\n(?![^<]*<\/code>)/g, '<br/>')
      return `<p>${processed}</p>`
    },
    [BLOCKS.HEADING_1]: (node, next) => `<h1>${next(node.content).replace(/\n/g, '<br/>')}</h1>`,
    [BLOCKS.HEADING_2]: (node, next) => `<h2>${next(node.content).replace(/\n/g, '<br/>')}</h2>`,
    [BLOCKS.HEADING_3]: (node, next) => `<h3>${next(node.content).replace(/\n/g, '<br/>')}</h3>`,
    [BLOCKS.HEADING_4]: (node, next) => `<h4>${next(node.content).replace(/\n/g, '<br/>')}</h4>`,
    [BLOCKS.HEADING_5]: (node, next) => `<h5>${next(node.content).replace(/\n/g, '<br/>')}</h5>`,
    [BLOCKS.HEADING_6]: (node, next) => `<h6>${next(node.content).replace(/\n/g, '<br/>')}</h6>`,
    // strip embedded tags from list items (contentful wraps everything in a <p> tag)
    [BLOCKS.LIST_ITEM]: (node, next) => `<li>${next(node.content).replace(/<\/?p>/g, '')}</li>`,
    // nested asset
    [BLOCKS.EMBEDDED_ASSET]: node => {
      const { description, file } = node.data.target.fields
      return `<img src="${file.url}?fm=webp" alt="${description}" style="max-width: 100%; object-fit:contain" />`
    },
    [INLINES.HYPERLINK]: (node, next) => {
      // TODO: keep for reference, but unused as of 2025-09-16
      // convert absolute URLs for the same host to relative paths
      // const escapedHost = window.location.host.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      // const regex = new RegExp(`^https?://${escapedHost}(/.*?)$`, 'gm')
      // let href = node.data.uri.replace(regex, '$1')
      const href = node.data.uri

      const isInternal = href.startsWith('/')

      // Add data-spa-route attribute for relative links
      return `<a ${isInternal ? 'data-spa-route' : ''} href="${href}" class="text-weight-medium text-link-blue__important" plain>${next(node.content)}</a>`
    },
  },
  renderMark: {
    // prepare code blocks for prismjs formatting
    [MARKS.CODE]: text => {
      const regex = /^lang:(.*)\b/
      // If the code snippet doesn't have the expected metadata
      if (!regex.test(text)) {
        return `<code>${text}</code>`
      }
      // Extract the language and action
      const [lang, action] = regex.exec(text)[1].split('/')

      const snippet = text.split('\n').slice(1).join('\n')

      const renderSnippet =
        action === 'render'
          ? new DOMParser().parseFromString(snippet.replace(/\n+/gm, ''), 'text/html').documentElement.textContent
          : null

      // Remove the first line to avoid including metadata in the rendered version
      let html = `<pre><code class="language-${lang}">${snippet}</code></pre>`

      if (renderSnippet) {
        html = `${renderSnippet}${html}`
      }

      return html
    },
  },
}

function renderRichText(content) {
  // if using Contentful's Rich Text input
  return documentToHtmlString(content, richRenderOptions)
}

/**
 * Parse and manipulate Product Grid Merch Cards data
 * @param {array} merchCards Array of merch cards
 * @returns {array} The parsed merch cards
 */
function parseMerchCards(merchCards) {
  return (merchCards || []).reduce((acc, { fields }) => {
    // unpublished modules don't have a `fields` prop
    if (fields) {
      acc.push({
        mobile: { src: get(fields.cardImageMbl, 'fields.file.url'), alt: fields.cardImageAlt },
        desktop: { src: get(fields.cardImageDsk, 'fields.file.url'), alt: fields.cardImageAlt },
        position: parseInt(fields.cardPosition) - 1,
        internalLinkPath: fields.internalLinkPath,
      })
    }
    return acc
  }, [])
}

/**
 * Parse and manipulate Product Grid Merch Rows data
 * @param {array} merchRows Array of merch rows
 * @returns {array} The parsed merch rows
 */
function parseMerchRows(merchRows) {
  return (merchRows || []).reduce((acc, mod) => {
    // unpublished modules don't have a `fields` prop
    if (mod) {
      // const { contentModules = [], position, ...data } = fields
      mod = parseEntry(mod)
      acc.push({
        ...mod,
        position: parseInt(mod.position) - 1,
      })
    }
    return acc
  }, [])
}

/**
 * Parse and maniputle the fields object for a contentful module
 * @param {object} fields The fields object from a contentful module
 * @param {string} moduleType The module type
 * @returns {object} The parsed fields object
 */
function parseFields(fields, moduleType) {
  switch (moduleType) {
    /* Accordion section */
    case 'accordionSection':
      // fields.learnMore = { text: fields.learnMoreText, path: fields.learnMorePath }
      fields.content = renderRichText(fields.content)
      fields.featuredImage = {
        src: get(fields.featuredImage, 'fields.file.url'),
        alt: get(fields.featuredImage, 'fields.description'),
      }
      break

    /* CTA BUTTON */
    case 'ctaButton':
      fields.to = { path: fields.internalLinkPath }
      fields.href = fields.externalLinkPath
      delete fields.internalLinkPath
      delete fields.externalLinkPath

      break

    /* GENERAL PRODUCT MERCH */
    case 'flexibleContentModule':
    case 'flexibleProductMerchModule':
    case 'productMerchModule':
    case 'slider':
      if (fields.productDataQuery) {
        fields.queryParams = fields.productDataQuery
        delete fields.productDataQuery
      }
      // fields.defaultSort = fields.algoliaDefaultSort
      // delete fields.algoliaQuery
      // delete fields.algoliaDefaultSort

      // Header
      fields.header = {}
      if (fields.headerText) {
        fields.header.richText = renderRichText(fields.headerText) || fields.headerText
        delete fields.headerText
      } else if (fields.headerImageDsk) {
        const alt = fields.headerImageAlt || 'Image'
        const linkTo = fields.headerImageLinkPath || undefined

        fields.header.image = {
          desktop: { src: get(fields.headerImageDsk, 'fields.file.url'), alt, linkTo },
          mobile: { src: get(fields.headerImageMbl, 'fields.file.url'), alt, linkTo },
        }
        delete fields.headerImageMbl
        delete fields.headerImageDsk
        delete fields.headerImageAlt
      }
      // Header Copy
      if (fields.supportingText || fields.headerSupportingText) {
        fields.header.copy = fields.supportingText || fields.headerSupportingText
        delete fields.supportingText
        delete fields.headerSupportingText
      }

      // Bug Image
      if (fields.bugImage) {
        fields.header.bug = {
          src: get(fields.bugImage, 'fields.file.url'),
          alt: fields.bugImageAlt,
        }
        delete fields.bugImage
        delete fields.bugImageAlt
      }

      // Device specific background Image
      if (fields.backgroundImageMbl || fields.backgroundImageDsk) {
        fields.backgroundImage = {
          mobile: { src: get(fields.backgroundImageMbl, 'fields.file.url'), alt: fields.backgroundImageAlt },
          desktop: { src: get(fields.backgroundImageDsk, 'fields.file.url'), alt: fields.backgroundImageAlt },
        }
        delete fields.backgroundImageMbl
        delete fields.backgroundImageDsk
        delete fields.backgroundImageAlt
      }

      if (fields.type === 'youTubeVideoModule') {
        fields.playButtonImage = get(fields.playButtonImage, 'fields.file.url')
      }

      if (fields.type === 'flexibleContentModule') {
        fields.contentWidth = fields.layout.replace(/^.*?([0-9]+)%.*$/g, '$1')
        fields.layout = /right/i.test(fields.layout) ? 'right' : 'left'

        if (fields.mainImage) {
          fields.mainImage = {
            src: get(fields.mainImage, 'fields.file.url'),
            path: fields.mainImageLinkPath,
            alt: fields.mainImageAlt,
          }
          delete fields.mainImageLinkPath
          delete fields.mainImageAlt
        }

        if (fields.ctaButton) {
          fields.ctaButton.to = { path: fields.ctaButton.path }
        }

        if (fields.bodyRichText) {
          fields.bodyRichText = renderRichText(fields.bodyRichText)
        }
      }

      if (fields.type === 'flexibleProductMerchModule') {
        fields.layout = /right/i.test(fields.layout) ? 'right' : 'left'
        fields.moduleType = kebabCase(fields.moduleType)
        fields.mainImage = {
          mobile: {
            src: get(fields.mainImageMbl, 'fields.file.url'),
            path: fields.mainImageLinkPath,
            alt: fields.mainImageAlt,
          },
          desktop: {
            src: get(fields.mainImageDsk, 'fields.file.url'),
            path: fields.mainImageLinkPath,
            alt: fields.mainImageAlt,
          },
        }

        delete fields.mainImageMbl
        delete fields.mainImageDsk
        delete fields.mainImageLinkPath
        delete fields.mainImageAlt

        if (fields.ctaButton) {
          fields.ctaButton.to = { path: fields.ctaButton.path }
        }
      }

      if (fields.type === 'slider') {
        fields.sliderBgImage = {
          desktop: {
            src: get(fields.sliderBgImageDsk, 'fields.file.url'),
            alt: get(fields.sliderBgImageDsk, 'fields.description'),
          },
          mobile: {
            src: get(fields.sliderBgImageMbl, 'fields.file.url'),
            alt: get(fields.sliderBgImageMbl, 'fields.description'),
          },
        }
        delete fields.sliderBgImageDsk
        delete fields.sliderBgImageMbl

        fields.slideBgImage = {
          desktop: {
            src: get(fields.slideBgImageDsk, 'fields.file.url'),
            alt: get(fields.slideBgImageDsk, 'fields.description'),
          },
          mobile: {
            src: get(fields.slideBgImageMbl, 'fields.file.url'),
            alt: get(fields.slideBgImageMbl, 'fields.description'),
          },
        }
        delete fields.slideBgImageDsk
        delete fields.slideBgImageMbl
      }
      break

    /* FAQs */
    case 'faqEntry':
      fields.learnMore = { text: fields.learnMoreText, path: fields.learnMorePath }
      fields.answer = renderRichText(fields.answer)
      delete fields.learnMoreText
      delete fields.learnMorePath

      break

    /* FEATURED CATEGORIES GRID */
    case 'featuredCategoriesGrid':
      fields.bugImage = { src: get(fields.bugImage, 'fields.file.url'), alt: fields.bugImageAlt }
      break

    /* Featured INRO/PRODUCT */
    case 'featuredInfoProductModule':
      fields.headerText = {
        mobile: fields.headerTextMbl,
        desktop: fields.headerTextDsk,
      }
      fields.mainCopy = {
        mobile: fields.mainCopyMbl,
        desktop: fields.mainCopyDsk,
      }
      fields.supportingCopy = {
        mobile: fields.supportingCopyMbl,
        desktop: fields.supportingCopyDsk,
      }
      fields.bugImage = {
        mobile: {
          src: get(fields.bugImageMbl, 'fields.file.url'),
          alt: get(fields.bugImageMbl, 'fields.description'),
        },
        desktop: {
          src: get(fields.bugImageDsk, 'fields.file.url'),
          alt: get(fields.bugImageDsk, 'fields.description'),
        },
      }
      fields.layout = {
        mobile: fields.layoutMbl,
        desktop: fields.layoutDsk,
      }
      fields.productImageOverride = {
        src: get(fields.productImageOverride, 'fields.file.url'),
        alt: get(fields.productImageOverride, 'fields.description'),
      }
      delete fields.layoutMbl
      delete fields.layoutDsk
      delete fields.headerTextMbl
      delete fields.headerTextDsk
      delete fields.mainCopyMbl
      delete fields.mainCopyDsk
      delete fields.supportingCopyMbl
      delete fields.supportingCopyDsk
      delete fields.bugImageMbl
      delete fields.bugImageDsk
      break

    /* HERO BANNER */
    case 'heroBannerModule':
      const desktopImage = get(fields.heroImageDsk, 'fields.file', {})
      const mobileImage = get(fields.heroImageMbl, 'fields.file', {})

      fields.heroImage = {
        desktop: { ...desktopImage, src: desktopImage.url, alt: fields.heroImageAlt },
        mobile: { ...mobileImage, src: mobileImage.url, alt: fields.heroImageAlt },
      }
      delete fields.heroImageMbl
      delete fields.heroImageDsk
      delete fields.heroImageAlt

      fields.bugImage = { src: get(fields.bugImage, 'fields.file.url'), alt: fields.bugImageAlt }

      break

    /* GENERIC MEDIA */
    case 'mediaModule':
      const mobileMedia = get(fields.mediaMbl, 'fields.file', {})
      const desktopMedia = get(fields.mediaDsk, 'fields.file', {})

      fields.mobile = {
        ...mobileMedia,
        src: mobileMedia.url,
        styles: fields.cssStylesMbl || {},
      }
      fields.desktop = {
        ...desktopMedia,
        src: desktopMedia.url,
        styles: fields.cssStylesDsk || {},
      }

      delete fields.cssStylesMbl
      delete fields.mediaMbl
      delete fields.mediaDsk
      delete fields.cssStylesDsk

      break

    /* OPEN CONTENT */
    case 'openContent':
      // if using Contentful's Rich Text input
      const openContent = renderRichText(fields.openContent)
      fields.openContent = {
        mobile: openContent,
        desktop: fields.openContentDsk ? renderRichText(fields.openContentDsk) : openContent,
      }
      delete fields.openContentDsk
      break

    case 'page':
      // console.log('page fields', fields)
      break

    case 'resultList':
      fields.merchCards = parseMerchCards(fields.merchCards)
      fields.merchRows = parseMerchRows(fields.merchRows)

      fields.ruleContexts = fields.slug
      fields.title = fields.header
      fields.sortable = fields.showSortFilter
      fields.facetable = fields.showSortFilter
      // fields.defaultSort = fields.algoliaDefaultSort
      fields.sortFilterAlignment = kebabCase(fields.sortFilterAlignment)
      // fields.facetChipStyles = fields.facetChipCss
      fields.queryFilters = ''

      // cleanup
      delete fields.showSortFilter
      delete fields.algoliaDefaultSort
      delete fields.facetChipCss
      delete fields.splitShoppingTabsOpenBoxLabel
      delete fields.splitShoppingTabsOpenBoxDescription
      delete fields.splitShoppingTabsDropShipLabel
      delete fields.splitShoppingTabsDropShipDescription
      break

    /* Testimonials */
    case 'testimonials':
      fields.headerImage = {
        mobile: { src: get(fields.headerImageMbl, 'fields.file.url'), alt: fields.headerImageAlt },
        desktop: { src: get(fields.headerImageDsk, 'fields.file.url'), alt: fields.headerImageAlt },
      }
      delete fields.headerImageMbl
      delete fields.headerImageDsk
      delete fields.headerImageAlt

      break
  }

  // account for any module-specific spacing
  if (fields.spacingClasses) {
    fields.classes = fields.spacingClasses.reduce((acc, c) => {
      acc[c] = true
      return acc
    }, {})
    delete fields.spacingClasses
  }

  return { ...fields }
}

/**
 * Recursively build out content modules
 * @param {object} mod The current/parent module
 * @returns {array} Array of parsed modules
 */
function parseEntry(entry) {
  entry = {
    ...entry.fields,
    id: get(entry, 'sys.id', null),
    type: get(entry.sys, 'contentType.sys.id', entry.type),
  }

  // debugLog('CONTENTFUL MODULE', mod.name, mod.type, mod.id, breakpoint.xs)

  const { contentModules = [], ...fields } = entry
  let modules = []

  if (contentModules.length) {
    // recusively build out child modules
    modules = contentModules.map(m => parseEntry(m))
  }

  return { modules, ...parseFields(fields, entry.type) }
}

/**
 * Parse the Contentful SDK client response
 * @param {object} response The Contentful SDK client response
 * @returns {object} Parsed content modules
 */
export function parseResponseEntry({ entry }) {
  // debugLog('CONTENTFUL RAW ENTRY', entry)

  if (entry) {
    try {
      const { modules, ...fields } = parseEntry(entry)
      // debugLog('CONTENTFUL RESPONSE - BUILT', { fields, modules })
      return { modules, ...fields }
    } catch (error) {
      $logger.error('CONTENTFUL RESPONSE - ERROR', error)
    }
  }

  return { modules: [] }
}
